import { useState, useCallback, useEffect } from 'react';
import supabase from '@/supabase';

interface UseBlockUserReturn {
  blockUser: (blockedUserId: string) => Promise<{ success: boolean; error?: string }>;
  unblockUser: (blockedUserId: string) => Promise<{ success: boolean; error?: string }>;
  isBlocked: boolean;
  isLoading: boolean;
  error: string | null;
}

export function useBlockUser(userId: string | undefined, otherUserId: string | undefined): UseBlockUserReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);

  // Check if user has blocked the other user
  useEffect(() => {
    const checkBlockStatus = async () => {
      if (!userId || !otherUserId) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('blocked_users')
          .select('id')
          .eq('blocker_id', userId)
          .eq('blocked_id', otherUserId)
          .maybeSingle();

        if (fetchError) {
          console.error('[useBlockUser] Error checking block status:', fetchError);
          return;
        }

        setIsBlocked(!!data);
      } catch (err) {
        console.error('[useBlockUser] Unexpected error checking block status:', err);
      }
    };

    checkBlockStatus();
  }, [userId, otherUserId]);

  const blockUser = useCallback(async (blockedUserId: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'User must be logged in to block users' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('blocked_users')
        .insert({
          blocker_id: userId,
          blocked_id: blockedUserId,
        });

      if (insertError) {
        // Check for duplicate block
        if (insertError.code === '23505') {
          setIsBlocked(true);
          return { success: true }; // Already blocked, treat as success
        }
        console.error('[useBlockUser] Block error:', insertError);
        const errorMessage = insertError.message || 'Failed to block user';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      setIsBlocked(true);
      return { success: true };
    } catch (err) {
      console.error('[useBlockUser] Unexpected block error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const unblockUser = useCallback(async (blockedUserId: string): Promise<{ success: boolean; error?: string }> => {
    if (!userId) {
      return { success: false, error: 'User must be logged in to unblock users' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('blocked_users')
        .delete()
        .eq('blocker_id', userId)
        .eq('blocked_id', blockedUserId);

      if (deleteError) {
        console.error('[useBlockUser] Unblock error:', deleteError);
        const errorMessage = deleteError.message || 'Failed to unblock user';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      setIsBlocked(false);
      return { success: true };
    } catch (err) {
      console.error('[useBlockUser] Unexpected unblock error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return {
    blockUser,
    unblockUser,
    isBlocked,
    isLoading,
    error,
  };
}
